import React, { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Clock, Send, Eye, ChevronDown, ChevronUp, AlarmClock } from "lucide-react";

const REASON_HEX = {
  sla_overdue: "#9C1E22", follow_up_overdue: "#B7791F",
  new_messages: "#2C6480", stalled: "#6B7280",
};

const fmtWhen = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
};

export default function DigestCard() {
  const [prefs, setPrefs] = useState(null);
  const [meta, setMeta] = useState({ timezone_effective: "", last_sent: null });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);

  const loadPrefs = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/ext-digest/preferences");
      setPrefs(data.preferences);
      setMeta({ timezone_effective: data.timezone_effective, last_sent: data.last_sent });
    } catch { /* staff-only; silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { loadPrefs(); }, []);

  const save = async (patch) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    setSaving(true);
    try {
      const { data } = await api.put("/ext-digest/preferences", patch);
      setPrefs(data.preferences);
      if (data.warning) toast.warning(data.warning);
      else toast.success("Digest settings saved.");
    } catch (e) { toast.error(errMsg(e)); loadPrefs(); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.get("/ext-digest/preview");
      setPreview(data);
      setOpen(true);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setPreviewing(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/ext-digest/send-now");
      if (data.sent) { toast.success(data.message || "Digest queued."); loadPrefs(); }
      else toast.info(data.message || "Nothing needs your attention right now.");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSending(false); }
  };

  if (loading || !prefs) return null;

  const counts = preview?.counts || {};
  const items = preview?.items || [];
  const labels = preview?.bucket_labels || {};
  const order = ["sla_overdue", "follow_up_overdue", "new_messages", "stalled"];

  return (
    <div className="rounded-xl border border-[#E6EDF2] bg-white mb-4" data-testid="ext-digest-card">
      <div className="flex items-center justify-between gap-3 flex-wrap p-3.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className="h-9 w-9 rounded-lg bg-[#1C4358]/10 flex items-center justify-center shrink-0">
            <AlarmClock size={17} className="text-[#1C4358]" />
          </div>
          <div className="min-w-0">
            <div className="text-[14px] font-bold text-[#1C4358] leading-tight">My External Referral Digest</div>
            <div className="text-[12px] text-[#6B7280]">
              A daily to-do of referrals that need a nudge or follow-up — emailed only when something needs attention.
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 text-[12px] text-[#252221] font-medium">
            <Switch data-testid="ext-digest-enabled" checked={!!prefs.enabled} disabled={saving}
              onCheckedChange={(v) => save({ enabled: v })} />
            {prefs.enabled ? "On" : "Off"}
          </label>
          <button onClick={() => setOpen((v) => !v)} data-testid="ext-digest-expand"
            className="text-[12px] text-[#1C4358] font-medium flex items-center gap-0.5 hover:underline">
            {open ? <>Hide <ChevronUp size={13} /></> : <>Settings <ChevronDown size={13} /></>}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-[#EEF2F5] p-3.5 space-y-3.5" data-testid="ext-digest-panel">
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="text-[11px] text-[#4B5563] flex items-center gap-1 mb-1"><Clock size={11} /> Delivery time (local)</label>
              <Input type="time" value={prefs.delivery_time || "07:00"} disabled={saving}
                onChange={(e) => setPrefs({ ...prefs, delivery_time: e.target.value })}
                onBlur={(e) => save({ delivery_time: e.target.value })}
                className="h-9 w-[130px] text-[13px]" data-testid="ext-digest-time" />
            </div>
            <div>
              <label className="text-[11px] text-[#4B5563] mb-1 block">Timezone (IANA, e.g. America/Chicago)</label>
              <Input value={prefs.timezone || ""} disabled={saving} placeholder="UTC (system default)"
                onChange={(e) => setPrefs({ ...prefs, timezone: e.target.value })}
                onBlur={(e) => save({ timezone: e.target.value })}
                className="h-9 w-[220px] text-[13px]" data-testid="ext-digest-tz" />
              <div className="text-[10px] text-[#9AA5B1] mt-0.5">Using: {meta.timezone_effective}</div>
            </div>
          </div>

          <div className="text-[11px] text-[#6B7280] bg-[#F4F8FB] border border-[#E6EDF2] rounded-lg px-2.5 py-2">
            You'll only be emailed when there's something that needs attention — no empty digests. This summary is
            internal-only and never messages partners automatically.
            {meta.last_sent && (
              <span className="block mt-1 text-[#1C4358]" data-testid="ext-digest-last-sent">
                Last digest: {fmtWhen(meta.last_sent.at)}{meta.last_sent.count != null ? ` · ${meta.last_sent.count} item(s)` : ""}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={runPreview} disabled={previewing}
              className="h-8 text-[12px] border-[#D6E0E7] text-[#1C4358] gap-1" data-testid="ext-digest-preview-btn">
              {previewing ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />} Preview my digest
            </Button>
            <Button size="sm" onClick={sendNow} disabled={sending}
              className="h-8 text-[12px] bg-[#1C4358] hover:bg-[#153341] text-white gap-1" data-testid="ext-digest-sendnow-btn">
              {sending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send me a digest now
            </Button>
          </div>

          {preview && (
            <div className="rounded-lg border border-[#EEF2F5] p-3" data-testid="ext-digest-preview">
              {preview.actionable ? (
                <>
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {order.filter((b) => counts[b]).map((b) => (
                      <span key={b} data-testid={`ext-digest-count-${b}`}
                        className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                        style={{ backgroundColor: `${REASON_HEX[b]}18`, color: REASON_HEX[b] }}>
                        {labels[b] || b}: {counts[b]}
                      </span>
                    ))}
                  </div>
                  <div className="space-y-1.5">
                    {items.slice(0, 12).map((it) => (
                      <a key={it.referral_id} href={it.link} data-testid={`ext-digest-item-${it.referral_id}`}
                        className="flex items-start justify-between gap-3 rounded-md px-2 py-1.5 hover:bg-[#F4F8FB] transition-colors">
                        <div className="min-w-0">
                          <div className="text-[13px] font-semibold text-[#1C4358] truncate">{it.organization}</div>
                          <div className="text-[11px] text-[#6B7280]">{it.account_type_label} · {it.status_label}</div>
                        </div>
                        <div className="text-[11px] font-medium shrink-0 text-right" style={{ color: REASON_HEX[it.buckets?.[0]] || "#6B7280" }}>
                          {it.reason}
                        </div>
                      </a>
                    ))}
                  </div>
                  {items.length > 12 && <div className="text-[11px] text-[#9AA5B1] mt-1.5">…and {items.length - 12} more.</div>}
                </>
              ) : (
                <div className="text-[13px] text-[#4B5563] flex items-center gap-2" data-testid="ext-digest-empty">
                  <Mail size={15} className="text-[#2F7D5A]" /> All clear — nothing needs your attention right now.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
